/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { Fragment } from "react"
import { Dialog as DialogPrimitive, Transition } from "@headlessui/react"
import cx from "clsx"
import CloseButton from "components/CloseButton"
import Button from "components/Forms/Button"
import Show from "components/Show"
import { noop } from "lib/helpers"
import { ExclamationCircleIcon } from "components/Icons"

export interface DialogProps {
  open: boolean
  onClose: () => void
  onClosed?: () => void
  closable?: boolean
  title: React.ReactNode | string
  children?: React.ReactNode
  className?: string
  confirmationText?: string
  onConfirmation?: () => void
  confirmationDisabled?: boolean
  cancelText?: string
  onCancel?: () => void
  cancelDisabled?: boolean
  testid?: string
}

const Dialog = ({
  open,
  onClose,
  onClosed,
  closable = true,
  title,
  children,
  className,
  confirmationText = `Confirm`,
  onConfirmation = noop,
  confirmationDisabled,
  cancelText = `Cancel`,
  onCancel = noop,
  cancelDisabled,
  testid
}: DialogProps) => (
  <Transition.Root
    // @ts-ignore
    as={Fragment}
    show={open}
    afterLeave={onClosed}
  >
    <DialogPrimitive
      onClose={() => closable && onClose()}
      className="relative z-50"
      {...(testid && { "data-testid": testid })}
    >
      <Transition.Child
        // @ts-ignore
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <DialogPrimitive.Backdrop className="fixed z-40 inset-0 bg-opacity-25 ui-not-open:backdrop-blur-0 ui-open:backdrop-blur-sm transition-all" />
      </Transition.Child>

      <div className="fixed z-50 inset-0 px-5 pt-28 pb-8 overflow-auto md:py-32 bg-black bg-opacity-50">
        <Transition.Child
          // @ts-ignore
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 -translate-y-1/4"
          enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 -translate-y-1/4"
        >
          <DialogPrimitive.Panel
            className={cx(
              `relative w-[600px] mx-auto p-6 max-w-full bg-white border border-primary-light-gray rounded-lg`,
              className
            )}
          >
            <Show when={closable}>
              <CloseButton
                className="absolute top-2 right-2"
                onClick={onClose}
                data-testid="close-dialog"
              />
            </Show>

            <div className="text-center">
              <ExclamationCircleIcon
                className="w-8 h-8 mb-4 inline-block"
                aria-hidden="true"
              />
            </div>

            <DialogPrimitive.Title className="font-medium text-lg text-center mb-4 md:text-xl">
              {title}
            </DialogPrimitive.Title>

            <div className="text-center mb-6">{children}</div>

            <div
              className={cx({
                "grid grid-rows-2 gap-4 grid-flow-dense md:grid-rows-1 md:grid-cols-2":
                  cancelText && confirmationText,
                "flex justify-center": !cancelText || !confirmationText
              })}
            >
              <Show when={cancelText}>
                <Button
                  variant="tertiary"
                  size="large"
                  className="row-start-2 md:row-start-1"
                  onClick={onCancel}
                  disabled={cancelDisabled}
                >
                  {cancelText}
                </Button>
              </Show>

              <Show when={confirmationText}>
                <Button
                  onClick={onConfirmation}
                  size="large"
                  className="row-start-1 w-full max-w-full"
                  disabled={confirmationDisabled}
                >
                  {confirmationText}
                </Button>
              </Show>
            </div>
          </DialogPrimitive.Panel>
        </Transition.Child>
      </div>
    </DialogPrimitive>
  </Transition.Root>
)

export default Dialog
